import { Button, Checkbox, Picker } from "@cntrl/ui";
import type {
  AdAccountAudienceView,
  AdLookalikeSourceView,
  AdLookalikeStatusView,
} from "../../../app/hooks/useAdCreator.js";
import { Segmented } from "./Segmented.js";
import { audienceLabel } from "./targetingFormat.js";
import { countryName, lookalikeStatusLine, lookalikeUnavailableLine } from "./lookalikeFormat.js";
import styles from "./LookalikeBlock.module.scss";

/** What a lookalike can mirror: the band's own fans (three engagement audiences we build), or an audience already
 *  on its ad account. */
export type LookalikeSource = "ig_fb_engagers" | "ig_engagers" | "fb_engagers" | "audience";

/** The sizes Ads Manager offers, in its order. 1% is the closest match, 10% the widest. */
export const LOOKALIKE_RATIOS = [1, 2, 3, 5, 10] as const;

const MIRROR_LABEL: Record<Exclude<LookalikeSource, "audience">, string> = {
  ig_fb_engagers: "Instagram + Facebook fans",
  ig_engagers: "Instagram fans",
  fb_engagers: "Facebook fans",
};

/** A size the way the handoff writes it — "~4,200" — exact below 100k, where a band can tell its own audiences
 *  apart by the number; the compact "1.2M" above it. Empty for no size. */
function approx(n: number | null | undefined): string {
  if (n == null || n <= 0) return "";
  return n < 100_000 ? `~${n.toLocaleString()}` : `~${audienceLabel(n)}`;
}

function peopleLabel(n: number | null | undefined): string {
  return approx(n) && `${approx(n)} people`;
}

/** Who a mirror's size counts: a lookalike copies everyone who engaged in the last year, not followers. */
const ENGAGED = "engaged in the last year";

/** One mirror row's sub-line — the four states are four different sentences (ART-295 §2). IG + FB fans is the two
 *  single mirrors together, so its size is theirs side by side rather than a sum that counts a fan of both twice. */
function mirrorSub(s: AdLookalikeSourceView, all: readonly AdLookalikeSourceView[]): string {
  switch (s.state) {
    case "ready": {
      if (s.source === "ig_fb_engagers") {
        const [ig, fb] = ["ig_engagers", "fb_engagers"].map((k) => all.find((x) => x.source === k)?.audienceSize);
        if (approx(ig) && approx(fb)) return `${approx(ig)} + ${approx(fb)} ${ENGAGED}`;
      }
      return approx(s.audienceSize) ? `${approx(s.audienceSize)} ${ENGAGED}` : "Ready";
    }
    case "too-small":
      return s.audienceSize != null ? `Not enough fans yet (${s.audienceSize})` : "Not enough fans yet";
    case "counting":
      return "Meta is counting your fans — check back soon";
    default:
      return s.source === "ig_fb_engagers"
        ? "Needs Instagram and a Facebook Page"
        : s.source === "ig_engagers"
          ? "No Instagram account connected"
          : "No Facebook Page connected";
  }
}

function audienceOptionLabel(a: AdAccountAudienceView): string {
  const kind = a.isLookalike ? " · lookalike" : "";
  return `${a.name ?? a.id}${kind} — ${peopleLabel(a.audienceSize) || "size unknown"}`;
}

/**
 * The Lookalike audience block — the first thing in the Audience section (ART-295). ON, Meta shows the ad to
 * people who look like what the band picks to MIRROR, and the manual interests / cities / states hide (countries
 * and ages stay: they are hard limits, and Meta needs a geo).
 *
 * **Eligibility is decided before the click, never at Push live.** Meta refuses a lookalike whose source has fewer
 * than 100 people, so a mirror or an ad-account audience under that is a DISABLED option saying why.
 *
 * **Every refusal says what it is.** A missing ads grant, no ad account, Meta's Custom Audience terms not accepted
 * and Meta not answering are four sentences with four fixes — never one "couldn't reach Meta".
 */
export function LookalikeBlock({
  enabled,
  onEnabledChange,
  sources,
  audiences,
  loading,
  error,
  apiStatus,
  apiCta,
  onRetry,
  onReconnect,
  source,
  audienceId,
  onSourceChange,
  ratio,
  onRatioChange,
  skipFollowers,
  onSkipFollowersChange,
  status,
  statusLoading,
}: {
  enabled: boolean;
  onEnabledChange: (on: boolean) => void;
  sources: AdLookalikeSourceView[];
  audiences: AdAccountAudienceView[];
  loading: boolean;
  /** The request itself failed (not a Meta verdict) — its message, already human. */
  error?: string | null;
  /** The Meta connection verdict the sources read came back with. */
  apiStatus?: string | null;
  /** The verdict's link — for the Custom Audience terms, Meta's own accept page. */
  apiCta?: string | null;
  onRetry: () => void;
  onReconnect: () => void;
  source: LookalikeSource | null;
  audienceId: string | null;
  onSourceChange: (source: LookalikeSource, audienceId?: string | null) => void;
  ratio: number;
  onRatioChange: (ratio: number) => void;
  skipFollowers: boolean;
  onSkipFollowersChange: (on: boolean) => void;
  status?: AdLookalikeStatusView | null;
  statusLoading?: boolean;
}) {
  const pickedAudience = audiences.find((a) => a.id === audienceId);
  const anyAudience = audiences.some((a) => a.eligible);
  const statusLine =
    source && (source !== "audience" || audienceId)
      ? statusLoading
        ? "Checking the lookalike…"
        : lookalikeStatusLine(status?.state, (status?.tooSmallCountries ?? []).map(countryName))
      : null;

  return (
    <div className={styles.block}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.title}>Lookalike audience</span>
          <p className={styles.hint}>
            Meta shows your ad to people who look like your own fans — and only them. &ldquo;On&rdquo; hides the
            interests, cities and states below; countries and ages stay.
          </p>
        </div>
        <div className={styles.toggle}>
          <Segmented
            ariaLabel="Lookalike audience"
            value={enabled ? "on" : "off"}
            onChange={(v) => onEnabledChange(v === "on")}
            options={[
              { value: "off", label: "Off" },
              { value: "on", label: "On" },
            ]}
          />
        </div>
      </div>

      {!enabled ? null : loading ? (
        <p className={styles.note}>Checking your audiences…</p>
      ) : error ? (
        <div className={styles.problem} role="status">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : apiStatus && apiStatus !== "ok" ? (
        <div className={styles.problem} role="status">
          <span>{lookalikeUnavailableLine(apiStatus)}</span>
          {apiStatus === "custom-audience-terms" && apiCta ? (
            <>
              <a className={styles.link} href={apiCta} target="_blank" rel="noreferrer">
                Accept Meta&apos;s terms
              </a>
              <Button variant="ghost" size="sm" onClick={onRetry}>
                I&apos;ve accepted — check again
              </Button>
            </>
          ) : apiStatus === "ads-permission-missing" || apiStatus === "not-connected" || apiStatus === "no-ad-account" ? (
            <Button variant="ghost" size="sm" onClick={onReconnect}>
              {apiStatus === "no-ad-account" ? "Open connections" : "Reconnect Meta"}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className={styles.group}>
            <span className={styles.label} id="lookalike-mirror-label">
              Mirror
            </span>
            <div className={styles.sources} role="radiogroup" aria-labelledby="lookalike-mirror-label">
              {sources.map((s) => {
                const key = s.source as Exclude<LookalikeSource, "audience">;
                const selected = source === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected || undefined}
                    disabled={!s.eligible}
                    className={styles.source}
                    onClick={() => onSourceChange(key)}
                  >
                    <span className={styles.sourceTitle}>{MIRROR_LABEL[key] ?? key}</span>
                    <span className={styles.sourceSub}>{mirrorSub(s, sources)}</span>
                  </button>
                );
              })}
              <button
                type="button"
                role="radio"
                aria-checked={source === "audience"}
                data-selected={source === "audience" || undefined}
                disabled={!anyAudience}
                className={styles.source}
                onClick={() => onSourceChange("audience", pickedAudience?.id ?? null)}
              >
                <span className={styles.sourceTitle}>An audience from my ad account</span>
                <span className={styles.sourceSub}>
                  {audiences.length === 0
                    ? "None on your ad account yet"
                    : anyAudience
                      ? `${audiences.length} on your ad account`
                      : "None with 100+ people yet"}
                </span>
              </button>
            </div>
            {source === "audience" ? (
              <Picker
                label="Audience from your ad account"
                value={audienceId ?? ""}
                placeholder="Pick an audience"
                onChange={(id) => onSourceChange("audience", id)}
                options={audiences.map((a) => ({
                  value: a.id ?? "",
                  label: audienceOptionLabel(a),
                  disabled: !a.eligible,
                }))}
                hint={
                  pickedAudience?.isLookalike
                    ? "That's already a lookalike — we'll use it as it is."
                    : "Audiences under 100 people can't seed a lookalike."
                }
              />
            ) : null}
          </div>

          <div className={styles.group}>
            <span className={styles.label}>Size</span>
            {source === "audience" && pickedAudience?.isLookalike ? (
              <p className={styles.note}>Set by the lookalike you picked.</p>
            ) : (
              <div className={styles.sizeControl}>
                <Segmented
                  ariaLabel="Lookalike size"
                  value={String(ratio)}
                  onChange={(v) => onRatioChange(Number(v))}
                  options={LOOKALIKE_RATIOS.map((r) => ({ value: String(r), label: `${r}%` }))}
                />
                <span className={styles.ratioScale}>
                  <span>1% · closest match</span>
                  <span>10% · widest</span>
                </span>
              </div>
            )}
          </div>

          {statusLine ? (
            <p className={styles.status} role="status" data-state={status?.state ?? undefined}>
              {statusLine}
            </p>
          ) : null}

          <Checkbox
            checked={skipFollowers}
            onChange={onSkipFollowersChange}
            label="Skip your existing fans"
          />
        </>
      )}
    </div>
  );
}
